import { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { productoService, Producto } from '../../services/productoService';
import { categoriaService } from '../../services/categoriaService';
import { useAuthStore } from '../../store/authStore';
import ProductCard from '../../components/Tienda/ProductCard';

const TiendaHome = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState<number | null>(null);

  // Obtener categorías activas
  const { data: categorias } = useQuery(
    ['categorias-tienda', user?.tenantId],
    () => categoriaService.getAll(user?.tenantId, null),
    {
      select: (data) => data.filter(cat => cat.isActive)
    }
  );

  // Obtener productos destacados (activos, ordenados por fecha de creación)
  const { data: productosDestacadosData } = useQuery(
    ['productos-destacados', user?.tenantId],
    () => productoService.getAll(1, 8, undefined, undefined, undefined, true),
    {
      select: (data) => data.data.productos.filter((p: Producto) => p.isActive)
    }
  );

  // Obtener productos recientes
  const { data: productosRecientesData } = useQuery(
    ['productos-recientes', user?.tenantId],
    () => productoService.getAll(1, 12, undefined, undefined, undefined, true),
    {
      select: (data) => data.data.productos
        .filter((p: Producto) => p.isActive)
        .sort((a: Producto, b: Producto) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        })
        .slice(0, 8)
    }
  );

  // Obtener productos filtrados por búsqueda/categoría
  const { data: productosFiltradosData } = useQuery(
    ['productos-filtrados', searchTerm, categoriaFiltro, user?.tenantId],
    () => productoService.getAll(1, 20, searchTerm || undefined, undefined, categoriaFiltro || undefined, true),
    {
      enabled: !!searchTerm || categoriaFiltro !== null,
      select: (data) => data.data.productos.filter((p: Producto) => p.isActive)
    }
  );

  const productosDestacados = productosDestacadosData || [];
  const productosRecientes = productosRecientesData || [];
  const productosFiltrados = productosFiltradosData || [];

  const mostrarFiltrados = searchTerm || categoriaFiltro !== null;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // La búsqueda se ejecuta automáticamente por el query
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section con Búsqueda */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Bienvenido a Nuestra Tienda
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">
              Descubre productos increíbles para ti
            </p>
            
            {/* Barra de búsqueda */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar productos..."
                  className="flex-1 px-6 py-4 rounded-lg text-gray-900 text-lg focus:outline-none focus:ring-2 focus:ring-white"
                />
                <button
                  type="submit"
                  className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
                >
                  Buscar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Categorías */}
        {categorias && categorias.length > 0 && (
          <section className="mb-12">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Categorías</h2>
              <button
                onClick={() => {
                  setCategoriaFiltro(null);
                  setSearchTerm('');
                }}
                className={`text-sm font-medium ${
                  categoriaFiltro === null && !searchTerm
                    ? 'text-primary-600'
                    : 'text-gray-600 hover:text-primary-600'
                }`}
              >
                Ver todas
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {categorias.slice(0, 6).map((categoria) => (
                <button
                  key={categoria.id}
                  onClick={() => {
                    setCategoriaFiltro(categoria.id);
                    setSearchTerm('');
                  }}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    categoriaFiltro === categoria.id
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-md'
                  }`}
                >
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-2 bg-primary-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                        />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {categoria.name}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Productos Filtrados (si hay búsqueda o categoría seleccionada) */}
        {mostrarFiltrados && (
          <section className="mb-12">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {searchTerm
                  ? `Resultados para "${searchTerm}"`
                  : categoriaFiltro
                  ? `Productos en esta categoría`
                  : 'Productos'}
              </h2>
              {productosFiltrados.length > 0 && (
                <span className="text-gray-600">
                  {productosFiltrados.length} producto{productosFiltrados.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {productosFiltrados.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {productosFiltrados.map((producto) => (
                  <ProductCard key={producto.id} producto={producto} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg">
                <svg
                  className="w-24 h-24 mx-auto text-gray-400 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-xl font-medium text-gray-900 mb-2">
                  No se encontraron productos
                </p>
                <p className="text-gray-600 mb-4">
                  Intenta con otros términos de búsqueda o explora nuestras categorías
                </p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setCategoriaFiltro(null);
                  }}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  Limpiar filtros
                </button>
              </div>
            )}
          </section>
        )}

        {/* Productos Destacados */}
        {!mostrarFiltrados && productosDestacados.length > 0 && (
          <section className="mb-12">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Productos Destacados</h2>
              <button
                onClick={() => navigate('/tienda/productos')}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Ver todos →
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {productosDestacados.map((producto) => (
                <ProductCard key={producto.id} producto={producto} />
              ))}
            </div>
          </section>
        )}

        {/* Productos Recientes */}
        {!mostrarFiltrados && productosRecientes.length > 0 && (
          <section className="mb-12">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Nuevos Productos</h2>
              <button
                onClick={() => navigate('/tienda/productos')}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Ver todos →
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {productosRecientes.map((producto) => (
                <ProductCard key={producto.id} producto={producto} />
              ))}
            </div>
          </section>
        )}

        {/* Mensaje si no hay productos */}
        {!mostrarFiltrados && productosDestacados.length === 0 && productosRecientes.length === 0 && (
          <div className="text-center py-16 bg-white rounded-lg">
            <svg
              className="w-24 h-24 mx-auto text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            <p className="text-xl font-medium text-gray-900 mb-2">
              No hay productos disponibles
            </p>
            <p className="text-gray-600">
              Pronto agregaremos productos increíbles para ti
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TiendaHome;

